import { UserType } from 'types/CustomerTypes';
import { Order } from 'types/OrderTypes';
import type { CustomerEditConsentsData } from '../../api/v1';
import {
  customerCreateTemporaryCustomerProfile,
  customerDeleteCustomer,
  customerEditConsents,
  customerEditProfile,
  customerForgotPassword,
  customerGetAvailableConsents,
  customerGetConstents,
  customerGetProfile,
  customerRefreshToken,
  customerUpdatePassword,
  customerUpdateTempUserProfile,
  customerVerifyCustomerCredentials,
} from '../../api/v1';
import { configureOpenApiClients } from '../../api/configure';
import { customerCreateCustomerProfileV2Async } from '../../api/v2';

export const useCustomerApi = (token: string, user: UserType, order: Order) => {
  configureOpenApiClients();

  const getProfile = async (paramToken: string = null) =>
    await customerGetProfile({ headers: { authorization: paramToken ? 'Bearer ' + paramToken : 'Bearer ' + token } });

  const createProfile = async (body) => await customerCreateCustomerProfileV2Async({ body });

  const editProfile = async (body) =>
    await customerEditProfile({
      headers: { authorization: 'Bearer ' + token },
      body,
    });

  const verifyCustomerCredentials = async (userData: { email: string; password: string }) =>
    await customerVerifyCustomerCredentials({ body: userData });

  const refreshToken = async () => await customerRefreshToken({ body: { token, refreshToken: user.refreshToken } });

  const getConsents = async () => await customerGetConstents({ headers: { authorization: 'Bearer ' + token } });

  const getAvailableConsents = async () => await customerGetAvailableConsents();

  const editConsents = async (consentData: { id: string; value: boolean }) => {
    const body = [
      {
        consentChoice: consentData.value,
        consentId: consentData.id,
      },
      { consents: user.consents },
    ];
    return (
      await customerEditConsents({
        body: body as unknown as CustomerEditConsentsData['body'],
        headers: { authorization: 'Bearer ' + token },
      })
    ).data.result;
  };

  const createTemporaryCustomerProfile = async () => await customerCreateTemporaryCustomerProfile();

  const updateTempProfile = async () =>
    await customerUpdateTempUserProfile({
      headers: { authorization: 'Bearer ' + token },
      body: {
        firstName: order.contactInformation.firstName,
        surname: order.contactInformation.surname,
        email: order.contactInformation.email,
        countryCode: order.contactInformation.telephoneNumberNoCountryCode
          ? order.contactInformation.countryCode
          : null,
        telephoneNumberNoCountryCode: order.contactInformation.telephoneNumberNoCountryCode
          ? order.contactInformation.telephoneNumberNoCountryCode
          : null,
      },
    });

  const forgotPassword = async (email: string) => await customerForgotPassword({ body: { email } });

  const updatePassword = async (data: { oldPwd: string; newPwd: string }) => {
    const body = {
      email: user.email,
      oldPassword: data.oldPwd,
      newPassword: data.newPwd,
    };
    return await customerUpdatePassword({ headers: { authorization: `Bearer ${token}` }, body });
  };

  const deleteCustomer = async () => await customerDeleteCustomer({ headers: { authorization: 'Bearer ' + token } });

  return {
    getProfile,
    updateTempProfile,
    editConsents,
    createTemporaryCustomerProfile,
    forgotPassword,
    verifyCustomerCredentials,
    refreshToken,
    updatePassword,
    deleteCustomer,
    getConsents,
    editProfile,
    createProfile,
    getAvailableConsents,
  };
};
